import { useParams, useNavigate } from 'react-router-dom';
import { useEffect, useState, useRef } from 'react';
import { ArrowLeft, ExternalLink, Clock, CheckCircle, XCircle, Loader2, Play, GitBranch } from 'lucide-react';
import { useProject } from '@/hooks/queries/useProject';
import { useDeployments } from '@/hooks/queries/useDeployments';
import { useBuildLogs } from '@/hooks/queries/useBuildLogs';
import { useTriggerBuild } from '@/hooks/mutations/useTriggerBuild';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import LoadingState from '@/components/common/LoadingState';
import { formatDistanceToNow } from 'date-fns';
import { Deployment } from '@/types/deployment';

export default function ProjectDetails() {
  const { id: projectId } = useParams();
  const navigate = useNavigate();
  const logsEndRef = useRef<HTMLDivElement>(null);
  const [selectedDeployment, setSelectedDeployment] = useState<Deployment | null>(null);
  const [stopPollingLogs, setStopPollingLogs] = useState(false);

  const { data: project, isLoading: isLoadingProject } = useProject(projectId);
  const { data: deployments = [], isLoading: isLoadingDeployments, refetch: refetchDeployments } = useDeployments(projectId);
  const triggerBuild = useTriggerBuild();
  
  // Find any active/building deployment
  const activeDeployment = deployments.find(d => d.status === 'building' || d.status === 'pending');
  const latestDeployment = deployments[0];
  
  // Auto-select active deployment or latest for logs
  useEffect(() => {
    if (activeDeployment) {
      setSelectedDeployment(activeDeployment);
    } else if (latestDeployment && !selectedDeployment) {
      setSelectedDeployment(latestDeployment);
    }
  }, [activeDeployment, latestDeployment, selectedDeployment]);
  
  // Poll for logs while building, or for 1 minute after completion
  const isBuilding = selectedDeployment?.status === 'building' || selectedDeployment?.status === 'pending';
  const shouldPollLogs = (isBuilding || !stopPollingLogs) && !!selectedDeployment;
  const { data: buildLogs = [] } = useBuildLogs(selectedDeployment?.id, shouldPollLogs);

  // Stop polling logs 1 minute after build completes
  useEffect(() => {
    if (selectedDeployment?.status === 'success' || selectedDeployment?.status === 'failed') {
      const timer = setTimeout(() => {
        setStopPollingLogs(true);
      }, 60000); // 1 minute

      return () => clearTimeout(timer);
    }
  }, [selectedDeployment?.status]);
  
  // Auto-scroll logs
  useEffect(() => {
    if (buildLogs.length > 0 && logsEndRef.current) {
      logsEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [buildLogs]);
  
  // Refetch deployments when build completes
  useEffect(() => {
    if (isBuilding) {
      const interval = setInterval(() => {
        refetchDeployments();
      }, 3000);
      return () => clearInterval(interval);
    }
  }, [isBuilding, refetchDeployments]);

  const handleTriggerBuild = async () => {
    if (!projectId || !project) return;
    try {
      await triggerBuild.mutateAsync({
        projectId,
        data: {
          commit_sha: 'HEAD',
          branch: project.branch || 'main',
        },
      });
      refetchDeployments();
    } catch (error) {
      console.error('Failed to trigger build:', error);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return (
          <Badge variant="secondary" className="flex items-center gap-1">
            <Clock className="h-3 w-3" />
            Pending
          </Badge>
        );
      case 'building':
        return (
          <Badge className="bg-yellow-500 flex items-center gap-1">
            <Loader2 className="h-3 w-3 animate-spin" />
            Building
          </Badge>
        );
      case 'success':
        return (
          <Badge className="bg-green-500 flex items-center gap-1">
            <CheckCircle className="h-3 w-3" />
            Success
          </Badge>
        );
      case 'failed':
        return (
          <Badge variant="destructive" className="flex items-center gap-1">
            <XCircle className="h-3 w-3" />
            Failed
          </Badge>
        );
      default:
        return <Badge>{status}</Badge>;
    }
  };

  const getLogColor = (log: { level: string; source: string }) => {
    if (log.level === 'error') return 'text-red-400';
    if (log.level === 'warn') return 'text-yellow-400';
    switch (log.source) {
      case 'system': return 'text-blue-400';
      case 'git': return 'text-green-400';
      case 'npm': return 'text-orange-400';
      case 'build': return 'text-purple-400';
      case 'deploy': return 'text-cyan-400';
      default: return 'text-gray-300';
    }
  };

  if (isLoadingProject || isLoadingDeployments) {
    return <LoadingState />;
  }

  if (!project) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600 mb-4">Project not found</p>
        <Button onClick={() => navigate('/')}>Go to Home</Button>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-4">
          <Button variant="outline" size="sm" onClick={() => navigate('/')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-3xl font-bold">{project.name}</h1>
            <div className="flex items-center gap-2 mt-1">
              <a
                href={project.github_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline flex items-center gap-1 text-sm"
              >
                {project.github_url.replace('https://github.com/', '')}
                <ExternalLink className="h-3 w-3" />
              </a>
              <Badge variant="outline" className="flex items-center gap-1">
                <GitBranch className="h-3 w-3" />
                {project.branch || 'main'}
              </Badge>
            </div>
          </div>
        </div>
        <Button onClick={handleTriggerBuild} disabled={triggerBuild.isPending || !!activeDeployment}>
          {triggerBuild.isPending || activeDeployment ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              {activeDeployment ? 'Building...' : 'Deploying...'}
            </>
          ) : (
            <>
              <Play className="h-4 w-4 mr-2" />
              Deploy
            </>
          )}
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Deployments List */}
        <div className="lg:col-span-1 space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Deployments</CardTitle>
              <CardDescription>Click to view logs</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2 max-h-[500px] overflow-y-auto">
              {deployments.length === 0 ? (
                <p className="text-gray-500 text-sm">No deployments yet</p>
              ) : (
                deployments.map((deployment) => (
                  <div
                    key={deployment.id}
                    onClick={() => setSelectedDeployment(deployment)}
                    className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                      selectedDeployment?.id === deployment.id
                        ? 'bg-blue-50 border-blue-300'
                        : 'hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      {getStatusBadge(deployment.status)}
                      <span className="text-xs text-gray-500">
                        {formatDistanceToNow(new Date(deployment.created_at), { addSuffix: true })}
                      </span>
                    </div>
                    <p className="text-xs font-mono text-gray-600 mt-2 truncate">
                      {deployment.commit_sha?.substring(0, 7) || 'HEAD'}
                    </p>
                    {deployment.preview_url && deployment.status === 'success' && (
                      <a
                        href={deployment.preview_url.startsWith('http') ? deployment.preview_url : `https://${deployment.preview_url}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="text-xs text-blue-600 hover:underline flex items-center gap-1 mt-1"
                      >
                        View <ExternalLink className="h-3 w-3" />
                      </a>
                    )}
                  </div>
                ))
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right Column - Build Logs */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Loader2 className={`h-4 w-4 ${isBuilding ? 'animate-spin' : ''}`} />
                Build Logs
                {selectedDeployment && (
                  <Badge variant="outline" className="ml-2 text-xs font-mono">
                    {selectedDeployment.id.substring(0, 20)}...
                  </Badge>
                )}
              </CardTitle>
              <CardDescription>
                {isBuilding
                  ? 'Live output from the build process (updates every 3 seconds)'
                  : selectedDeployment
                  ? `Build output for deployment`
                  : 'Select a deployment to view logs'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="bg-black text-green-400 font-mono text-xs p-4 rounded-lg h-[500px] overflow-y-auto">
                {!selectedDeployment ? (
                  <div className="text-gray-500 italic">
                    Select a deployment from the list to view its build logs
                  </div>
                ) : buildLogs.length === 0 ? (
                  <div className="text-gray-500 italic">
                    {isBuilding ? 'Waiting for logs...' : 'No logs available for this deployment'}
                  </div>
                ) : (
                  <div className="space-y-1">
                    {buildLogs.map((log, index) => (
                      <div key={log.id || index} className="flex gap-2">
                        <span className="text-gray-500 select-none shrink-0">
                          {new Date(log.timestamp).toLocaleTimeString()}
                        </span>
                        <span className={`text-gray-400 select-none shrink-0 w-16`}>
                          [{log.source}]
                        </span>
                        <span className={`break-all ${getLogColor(log)}`}>
                          {log.message}
                        </span>
                      </div>
                    ))}
                    <div ref={logsEndRef} />
                  </div>
                )}
              </div>
              {isBuilding && (
                <p className="text-xs text-gray-500 mt-2">
                  <Loader2 className="h-3 w-3 animate-spin inline mr-1" />
                  Fetching new logs...
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Latest Deployment Preview */}
      {latestDeployment?.status === 'success' && latestDeployment.preview_url && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-500" />
              Latest Deployment
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Your site is live at:</p>
                <a
                  href={latestDeployment.preview_url.startsWith('http') ? latestDeployment.preview_url : `https://${latestDeployment.preview_url}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline flex items-center gap-1"
                >
                  {latestDeployment.preview_url}
                  <ExternalLink className="h-4 w-4" />
                </a>
              </div>
              <Button
                onClick={() => {
                  const url = latestDeployment.preview_url?.startsWith('http')
                    ? latestDeployment.preview_url
                    : `https://${latestDeployment.preview_url}`;
                  window.open(url, '_blank');
                }}
              >
                Open Site
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
